import { Data, Effect } from 'effect';
import { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import { getPdfjs } from '@/features/_shared/pdf/pdfjs';
import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist';
import {
  CREATE_PDF_PREVIEW_MAX_PAGES,
  CREATE_PDF_PREVIEW_MAX_WIDTH,
  CREATE_SHEET_PREVIEW_MAX_COLS,
  CREATE_SHEET_PREVIEW_MAX_ROWS,
  CREATE_SHEET_PREVIEW_MAX_SHEETS,
  CREATE_TEXT_PREVIEW_MAX_CHARS,
  MAX_INLINE_CREATE_FILE_BYTES,
  MIME_BY_EXTENSION,
  SPREADSHEET_EXTENSIONS,
  TEXT_LIKE_EXTENSIONS,
} from './create-constants';
import type {
  CreateProjectFileEntry,
  CreateProjectFilePreview,
  CreateProjectFileResponse,
  CreateProjectPdfPreviewPage,
  CreateFileEncoding,
  CreateSessionFile,
  RenderedPdfPage,
} from './create-types';

let createMammothPromise: Promise<typeof import('mammoth')> | null = null;
let createXlsxPromise: Promise<typeof import('xlsx')> | null = null;

export function formatBytes(value: number | null | undefined): string {
  if (!value || value <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = value;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size >= 10 || unit === 0 ? Math.round(size) : size.toFixed(1)} ${units[unit]}`;
}

export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [delayMs, value]);

  return debounced;
}

export function compactDeliveryUrl(value: string | null | undefined): string {
  if (!value) return '-';
  return value.replace(/^https?:\/\//, '');
}

export function fileExtension(filename: string): string {
  const parts = filename.toLowerCase().split('.');
  return parts.length > 1 ? (parts[parts.length - 1] ?? '') : '';
}

export function inferMimeType(file: File): string {
  const explicit = file.type.trim().toLowerCase();
  if (explicit) return explicit;
  return MIME_BY_EXTENSION[fileExtension(file.name)] ?? 'application/octet-stream';
}

export function isPdfFile(file: File): boolean {
  return inferMimeType(file) === 'application/pdf' || fileExtension(file.name) === 'pdf';
}

export function isTextLikeFile(mime: string, filename: string): boolean {
  if (mime.startsWith('text/')) return true;
  if (
    [
      'application/json',
      'application/ld+json',
      'application/xml',
      'application/yaml',
      'application/javascript',
      'application/typescript',
      'image/svg+xml',
    ].includes(mime)
  ) {
    return true;
  }
  return TEXT_LIKE_EXTENSIONS.has(fileExtension(filename));
}

export function safeWorkspaceFileName(filename: string): string {
  const trimmed = filename.trim() || 'asset';
  return (
    trimmed
      .replace(/[/\\]+/g, '-')
      .replace(/[^A-Za-z0-9._ -]+/g, '-')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 120) || 'asset'
  );
}

export function buildCreateAssetPath(localId: string, filename: string): string {
  return `assets/${localId.slice(0, 8)}-${safeWorkspaceFileName(filename)}`;
}

export function buildProjectDocumentAssetPath(documentId: string, filename: string): string {
  return `documents/${documentId}-${safeWorkspaceFileName(filename)}`;
}

export function buildProjectFileAssetPath(fileId: string, filename: string): string {
  return `files/${fileId}-${safeWorkspaceFileName(filename)}`;
}

export function truncateForPrompt(
  value: string,
  maxChars: number,
): { text: string; truncated: boolean } {
  if (value.length <= maxChars) return { text: value, truncated: false };
  return { text: value.slice(0, maxChars), truncated: true };
}

function encodeArrayBufferBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let index = 0; index < bytes.length; index += 8192) {
    binary += String.fromCharCode(...bytes.subarray(index, index + 8192));
  }
  return btoa(binary);
}
export async function arrayBufferToBase64(buffer: ArrayBuffer): Promise<string> {
  return encodeArrayBufferBase64(buffer);
}

export class CreateFileBoundaryError extends Data.TaggedError('CreateFileBoundaryError')<{
  readonly operation: 'read-inline-file' | 'preview' | 'pdf-render';
}> {}

const createFileBoundaryCauses = new WeakMap<CreateFileBoundaryError, unknown>();

function createFileBoundaryFailure(
  cause: unknown,
  operation: CreateFileBoundaryError['operation'] = 'read-inline-file',
): CreateFileBoundaryError {
  const error = new CreateFileBoundaryError({ operation });
  createFileBoundaryCauses.set(error, cause);
  return error;
}

const attemptCreateFile = <A>(
  evaluate: () => A,
  operation: CreateFileBoundaryError['operation'] = 'preview',
) => Effect.try({ try: evaluate, catch: (cause) => createFileBoundaryFailure(cause, operation) });
const attemptCreateFilePromise = <A>(
  evaluate: () => PromiseLike<A>,
  operation: CreateFileBoundaryError['operation'] = 'preview',
) =>
  Effect.tryPromise({
    try: evaluate,
    catch: (cause) => createFileBoundaryFailure(cause, operation),
  });
const runCreateFile = <A>(
  program: Effect.Effect<A, CreateFileBoundaryError>,
  options?: { signal?: AbortSignal },
) => Effect.runPromise(restoreCreateFileBoundaryError(program), options);

export function restoreCreateFileBoundaryError<A>(
  program: Effect.Effect<A, CreateFileBoundaryError>,
): Effect.Effect<A, unknown> {
  return program.pipe(
    Effect.catch((error) => Effect.fail(createFileBoundaryCauses.get(error) ?? error)),
  );
}

export const createInlineSessionFileEffect = Effect.fn('web.createFiles.readInline')(function* (
  file: File,
  localId: string,
): Effect.fn.Return<CreateSessionFile, CreateFileBoundaryError> {
  const info = yield* attemptCreateFile(() => {
    if (file.size > MAX_INLINE_CREATE_FILE_BYTES) {
      throw new Error(
        `Non-PDF create assets are capped at ${formatBytes(MAX_INLINE_CREATE_FILE_BYTES)} for now.`,
      );
    }
    const mime = inferMimeType(file);
    return {
      mime,
      path: buildCreateAssetPath(localId, file.name),
      encoding: (isTextLikeFile(mime, file.name) ? 'utf-8' : 'base64') as CreateFileEncoding,
    };
  }, 'read-inline-file');
  const content =
    info.encoding === 'utf-8'
      ? yield* attemptCreateFilePromise(() => file.text(), 'read-inline-file')
      : yield* attemptCreateFilePromise(() => file.arrayBuffer(), 'read-inline-file').pipe(
          Effect.flatMap((buffer) =>
            attemptCreateFile(() => encodeArrayBufferBase64(buffer), 'read-inline-file'),
          ),
        );
  return yield* attemptCreateFile(
    () => ({
      path: info.path,
      name: file.name,
      mime: info.mime,
      size: file.size,
      content,
      encoding: info.encoding,
      metadata: {
        clientAssetId: localId,
      },
    }),
    'read-inline-file',
  );
});

export function createInlineSessionFile(
  file: File,
  localId: string,
  options?: { signal?: AbortSignal },
): Promise<CreateSessionFile> {
  return runCreateFile(createInlineSessionFileEffect(file, localId), options);
}

export function loadCreatePdfJs() {
  return getPdfjs();
}

export function loadCreateMammoth(): Promise<typeof import('mammoth')> {
  createMammothPromise ??= import('mammoth');
  return createMammothPromise;
}

export function loadCreateXlsx(): Promise<typeof import('xlsx')> {
  createXlsxPromise ??= import('xlsx');
  return createXlsxPromise;
}

export function base64ToBytes(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

export function base64ToArrayBuffer(value: string): ArrayBuffer {
  const bytes = base64ToBytes(value);
  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);
  return buffer;
}

export function projectFileExtension(path: string): string {
  const normalized = path.split(/[?#]/)[0]?.toLowerCase() ?? '';
  const fileName = normalized.split('/').pop() ?? normalized;
  const dotIndex = fileName.lastIndexOf('.');
  return dotIndex >= 0 ? fileName.slice(dotIndex + 1) : '';
}

export function normalizeMimeType(value: string | null | undefined): string {
  return (value ?? '').split(';')[0]?.trim().toLowerCase() ?? '';
}

export function inferProjectFilePreviewMime(
  file: CreateProjectFileEntry,
  response: CreateProjectFileResponse,
): string {
  const explicit = normalizeMimeType(response.info?.mimeType || file.mimeType);
  if (explicit) return explicit;
  return MIME_BY_EXTENSION[projectFileExtension(response.path ?? file.path)] ?? '';
}

export function isTextPreviewFile(mime: string, extension: string): boolean {
  return (
    mime.startsWith('text/') ||
    mime.includes('json') ||
    mime.includes('xml') ||
    mime.includes('yaml') ||
    TEXT_LIKE_EXTENSIONS.has(extension)
  );
}

export function isImagePreviewFile(mime: string, extension: string): boolean {
  return (
    mime.startsWith('image/') || ['gif', 'jpeg', 'jpg', 'png', 'svg', 'webp'].includes(extension)
  );
}

export function isPdfPreviewFile(mime: string, extension: string): boolean {
  return mime === 'application/pdf' || extension === 'pdf';
}

export function isSpreadsheetPreviewFile(mime: string, extension: string): boolean {
  return (
    SPREADSHEET_EXTENSIONS.has(extension) ||
    mime.includes('spreadsheet') ||
    mime.includes('excel') ||
    mime === 'text/csv' ||
    mime === 'text/tab-separated-values'
  );
}

export function isDocxPreviewFile(mime: string, extension: string): boolean {
  return (
    extension === 'docx' ||
    mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  );
}

export function decodeCreateTextContent(response: CreateProjectFileResponse): string {
  const content = response.content ?? '';
  if (response.encoding === 'base64') {
    return new TextDecoder('utf-8').decode(base64ToBytes(content));
  }
  return content;
}

export function trimCreateTextPreview(content: string): { content: string; truncated: boolean } {
  if (content.length <= CREATE_TEXT_PREVIEW_MAX_CHARS) return { content, truncated: false };
  return {
    content: content.slice(0, CREATE_TEXT_PREVIEW_MAX_CHARS),
    truncated: true,
  };
}

export function formatCreateSheetCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return value.toLocaleString();
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

const withCreatePdfEffect = Effect.fn('web.createFiles.pdf')(function* <A>(
  source: { url: string } | { data: Uint8Array },
  use: (pdf: PDFDocumentProxy) => Effect.Effect<A, CreateFileBoundaryError>,
) {
  const pdfjs = yield* attemptCreateFilePromise(() => loadCreatePdfJs(), 'pdf-render');
  let cleanupError: CreateFileBoundaryError | undefined;
  const result = yield* Effect.gen(function* () {
    const task = yield* Effect.acquireRelease(
      attemptCreateFile(() => {
        const task = pdfjs.getDocument(source);
        void task.promise.catch(() => undefined);
        return task;
      }, 'pdf-render'),
      (task) =>
        attemptCreateFilePromise(() => task.destroy(), 'pdf-render').pipe(
          Effect.catch((error) =>
            Effect.sync(() => {
              cleanupError = error;
            }),
          ),
        ),
    );
    const pdf = yield* attemptCreateFilePromise(() => task.promise, 'pdf-render');
    return yield* use(pdf);
  }).pipe(Effect.scoped);
  if (cleanupError) return yield* Effect.fail(cleanupError);
  return result;
});

type CreatePageCanvas = { canvas: HTMLCanvasElement; width: number; height: number };
const renderCreatePageEffect = Effect.fn('web.createFiles.page')(function* <A>(
  pdf: PDFDocumentProxy,
  pageNumber: number,
  scaleForPage: (page: PDFPageProxy) => number,
  ratio: number,
  use: (page: CreatePageCanvas) => Effect.Effect<A, CreateFileBoundaryError>,
  retainCanvas = false,
  missingContextMessage = 'Unable to render PDF preview',
) {
  const page = yield* attemptCreateFilePromise(() => pdf.getPage(pageNumber), 'pdf-render');
  const viewport = yield* attemptCreateFile(
    () => page.getViewport({ scale: scaleForPage(page) }),
    'pdf-render',
  );
  let adopted = false;
  const canvas = yield* Effect.acquireRelease(
    attemptCreateFile(() => document.createElement('canvas'), 'pdf-render'),
    (canvas) =>
      Effect.sync(() => {
        if (!adopted) {
          canvas.width = 0;
          canvas.height = 0;
        }
      }),
  );
  const context = yield* attemptCreateFile(() => {
    const context = canvas.getContext('2d');
    if (!context) throw new Error(missingContextMessage);
    canvas.width = Math.max(1, Math.floor(viewport.width * ratio));
    canvas.height = Math.max(1, Math.floor(viewport.height * ratio));
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    return context;
  }, 'pdf-render');
  let rendered = false;
  const task = yield* Effect.acquireRelease(
    attemptCreateFile(() => {
      const task = page.render({ canvas, canvasContext: context, viewport });
      void task.promise.catch(() => undefined);
      return task;
    }, 'pdf-render'),
    (task) =>
      rendered
        ? Effect.void
        : attemptCreateFile(() => task.cancel(), 'pdf-render').pipe(
            Effect.ignore,
            Effect.andThen(
              attemptCreateFilePromise(() => task.promise, 'pdf-render').pipe(Effect.ignore),
            ),
          ),
  );
  yield* attemptCreateFilePromise(() => task.promise, 'pdf-render');
  rendered = true;
  const result = yield* use({ canvas, width: viewport.width, height: viewport.height });
  adopted = retainCanvas;
  return result;
}, Effect.scoped);

export const renderCreatePdfPreviewEffect = Effect.fn('web.createFiles.pdfPreview')(function* (
  response: CreateProjectFileResponse,
): Effect.fn.Return<CreateProjectFilePreview, CreateFileBoundaryError> {
  const data = yield* attemptCreateFile(() => base64ToBytes(response.content ?? ''));
  return yield* withCreatePdfEffect({ data }, (pdf) =>
    Effect.gen(function* () {
      const pages: CreateProjectPdfPreviewPage[] = [];
      const pageLimit = Math.min(pdf.numPages, CREATE_PDF_PREVIEW_MAX_PAGES);
      for (let pageNumber = 1; pageNumber <= pageLimit; pageNumber += 1) {
        const ratio = yield* attemptCreateFile(() => Math.min(window.devicePixelRatio || 1, 2));
        const preview = yield* renderCreatePageEffect(
          pdf,
          pageNumber,
          (page) => {
            const viewport = page.getViewport({ scale: 1 });
            return Math.min(1.6, CREATE_PDF_PREVIEW_MAX_WIDTH / Math.max(viewport.width, 1));
          },
          ratio,
          (page) =>
            attemptCreateFile(() => ({
              pageNumber,
              dataUrl: page.canvas.toDataURL('image/png'),
              width: page.width,
              height: page.height,
            })),
        );
        pages.push(preview);
      }
      return {
        kind: 'pdf' as const,
        pages,
        pageCount: pdf.numPages,
        truncated: pdf.numPages > pages.length,
      };
    }),
  );
});
export function renderCreatePdfPreview(
  response: CreateProjectFileResponse,
  options?: { signal?: AbortSignal },
): Promise<CreateProjectFilePreview> {
  return runCreateFile(renderCreatePdfPreviewEffect(response), options);
}

export const renderCreateDocxPreviewEffect = Effect.fn('web.createFiles.docxPreview')(function* (
  response: CreateProjectFileResponse,
): Effect.fn.Return<CreateProjectFilePreview, CreateFileBoundaryError> {
  const mammoth = yield* attemptCreateFilePromise(() => loadCreateMammoth());
  const arrayBuffer = yield* attemptCreateFile(() => base64ToArrayBuffer(response.content ?? ''));
  const result = yield* attemptCreateFilePromise(() =>
    mammoth.default.convertToHtml({ arrayBuffer }),
  );
  return yield* attemptCreateFile(() => ({
    kind: 'docx' as const,
    html: DOMPurify.sanitize(result.value, { USE_PROFILES: { html: true } }),
    messages: result.messages
      .map((message) => message.message ?? '')
      .filter(Boolean)
      .slice(0, 3),
  }));
});
export function renderCreateDocxPreview(
  response: CreateProjectFileResponse,
  options?: { signal?: AbortSignal },
): Promise<CreateProjectFilePreview> {
  return runCreateFile(renderCreateDocxPreviewEffect(response), options);
}

export const renderCreateSpreadsheetPreviewEffect = Effect.fn('web.createFiles.sheetPreview')(
  function* (
    response: CreateProjectFileResponse,
    extension: string,
  ): Effect.fn.Return<CreateProjectFilePreview, CreateFileBoundaryError> {
    const XLSX = yield* attemptCreateFilePromise(() => loadCreateXlsx());
    return yield* attemptCreateFile(() => {
      const content = response.content ?? '';
      const workbook =
        response.encoding === 'base64'
          ? XLSX.read(base64ToBytes(content), { type: 'array', cellDates: true })
          : XLSX.read(content, {
              type: 'string',
              cellDates: true,
              FS: extension === 'tsv' ? '\t' : undefined,
            });
      let truncated = workbook.SheetNames.length > CREATE_SHEET_PREVIEW_MAX_SHEETS;
      const sheets = workbook.SheetNames.slice(0, CREATE_SHEET_PREVIEW_MAX_SHEETS).map(
        (sheetName) => {
          const sheet = workbook.Sheets[sheetName];
          const rawRows = (
            sheet
              ? XLSX.utils.sheet_to_json<unknown[]>(sheet, {
                  header: 1,
                  blankrows: false,
                  defval: '',
                })
              : []
          ) as unknown[][];
          const totalRows = rawRows.length;
          const totalCols = rawRows.reduce((max, row) => Math.max(max, row.length), 0);
          if (
            totalRows > CREATE_SHEET_PREVIEW_MAX_ROWS ||
            totalCols > CREATE_SHEET_PREVIEW_MAX_COLS
          ) {
            truncated = true;
          }
          const colLimit = Math.min(totalCols, CREATE_SHEET_PREVIEW_MAX_COLS);
          const rows = rawRows
            .slice(0, CREATE_SHEET_PREVIEW_MAX_ROWS)
            .map((row) =>
              Array.from({ length: colLimit }, (_, index) => formatCreateSheetCell(row[index])),
            );
          return {
            name: sheetName,
            rows,
            totalRows,
            totalCols,
          };
        },
      );

      return { kind: 'spreadsheet' as const, sheets, truncated };
    });
  },
);
export function renderCreateSpreadsheetPreview(
  response: CreateProjectFileResponse,
  extension: string,
  options?: { signal?: AbortSignal },
): Promise<CreateProjectFilePreview> {
  return runCreateFile(renderCreateSpreadsheetPreviewEffect(response, extension), options);
}

export const createProjectFilePreviewEffect = Effect.fn('web.createFiles.preview')(function* (
  file: CreateProjectFileEntry,
  response: CreateProjectFileResponse,
): Effect.fn.Return<CreateProjectFilePreview, CreateFileBoundaryError> {
  const { content, extension, mime } = yield* attemptCreateFile(() => ({
    content: response.content ?? '',
    extension: projectFileExtension(response.path ?? file.path),
    mime: inferProjectFilePreviewMime(file, response),
  }));
  if (!content) return { kind: 'unsupported', message: 'No preview content returned.' };

  if (isSpreadsheetPreviewFile(mime, extension)) {
    return yield* renderCreateSpreadsheetPreviewEffect(response, extension);
  }
  if (response.encoding === 'base64' && isDocxPreviewFile(mime, extension)) {
    return yield* renderCreateDocxPreviewEffect(response);
  }
  if (response.encoding === 'base64' && isPdfPreviewFile(mime, extension)) {
    return yield* renderCreatePdfPreviewEffect(response);
  }
  return yield* attemptCreateFile((): CreateProjectFilePreview => {
    if (response.encoding === 'base64' && isImagePreviewFile(mime, extension)) {
      return {
        kind: 'image',
        dataUrl: `data:${mime || 'application/octet-stream'};base64,${content}`,
        mime: mime || 'application/octet-stream',
        size: response.info?.size ?? file.size,
      };
    }
    if (response.encoding !== 'base64' || isTextPreviewFile(mime, extension)) {
      const text = trimCreateTextPreview(decodeCreateTextContent(response));
      return {
        kind: 'text',
        content: text.content,
        truncated: text.truncated,
      };
    }

    return {
      kind: 'unsupported',
      message: `Preview unavailable for ${mime || extension || 'this file'} (${formatBytes(response.info?.size ?? file.size)}).`,
    };
  });
});
export function createProjectFilePreview(
  file: CreateProjectFileEntry,
  response: CreateProjectFileResponse,
  options?: { signal?: AbortSignal },
): Promise<CreateProjectFilePreview> {
  return runCreateFile(createProjectFilePreviewEffect(file, response), options);
}

export const renderPdfPageCanvasEffect = Effect.fn('web.createFiles.pageCanvas')(function* (
  pdfDoc: PDFDocumentProxy,
  pageNumber: number,
  maxWidth: number,
  maxHeight: number,
  ratio: number,
) {
  return yield* renderCreatePageEffect(
    pdfDoc,
    pageNumber,
    (page) => {
      const viewport = page.getViewport({ scale: 1 });
      return Math.max(0.1, Math.min(maxWidth / viewport.width, maxHeight / viewport.height));
    },
    ratio,
    Effect.succeed,
    true,
    'Canvas unavailable',
  );
});
export function renderPdfPageCanvas(
  pdfDoc: PDFDocumentProxy,
  pageNumber: number,
  maxWidth: number,
  maxHeight: number,
  ratio: number,
  options?: { signal?: AbortSignal },
): Promise<CreatePageCanvas> {
  return runCreateFile(
    renderPdfPageCanvasEffect(pdfDoc, pageNumber, maxWidth, maxHeight, ratio),
    options,
  );
}
export const renderPdfPagesDataUrlsEffect = Effect.fn('web.createFiles.pdfPages')(function* (
  pdfUrl: string,
) {
  return yield* withCreatePdfEffect({ url: pdfUrl }, (pdf) =>
    Effect.gen(function* () {
      const pages: RenderedPdfPage[] = [];
      for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
        pages.push(
          yield* renderCreatePageEffect(
            pdf,
            pageNumber,
            (page) => {
              const viewport = page.getViewport({ scale: 1 });
              return Math.max(0.1, Math.min(760 / viewport.width, 1040 / viewport.height));
            },
            1,
            (page) =>
              attemptCreateFile(() => ({
                pageNumber,
                url: page.canvas.toDataURL('image/png'),
                width: page.width,
                height: page.height,
              })),
            false,
            'Canvas unavailable',
          ),
        );
      }
      return pages;
    }),
  );
});
export function renderPdfPagesDataUrls(
  pdfUrl: string,
  options?: { signal?: AbortSignal },
): Promise<RenderedPdfPage[]> {
  return runCreateFile(renderPdfPagesDataUrlsEffect(pdfUrl), options);
}
